import FingerprintJS from '@fingerprintjs/fingerprintjs';
import type { AuthenticationResponseJSON } from '@simplewebauthn/typescript-types';
import { FormikHelpers } from 'formik';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { showToast } from '@app/providers/ToastProvider';
import { useAppSelector } from '@app/redux/hooks';
import { getMFAPreconditionData } from '@app/redux/mfa/mfa.selectors';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import MFA_METHODS_ENUM from '@pages/Verification/MFASetupPage/enums/MFA_METHODS.enum';

const mfaCodeKey = {
	[MFA_METHODS_ENUM.WEBAUTHN]: 'yubico_webauthn',
	[MFA_METHODS_ENUM.EMAIL]: 'email',
	[MFA_METHODS_ENUM.SMS]: 'phone',
	[MFA_METHODS_ENUM.TOTP]: 'totp',
};

export const use2FALogin = ({ method }: { method: MFA_METHODS_ENUM }) => {
	const { startRequest: getMFACode, isPending: isSendingMFACode } = useTriggeredRequest(
		AccountClientRequests.MFA.getVerificationMFACode,
	);
	const { startRequest: loginWithMFA, isPending: isLoggingIn } = useTriggeredRequest(AccountClientRequests.Auth.login);
	const mfaPreconditionData = useAppSelector(getMFAPreconditionData);
	const generateRoutePath = useGenerateRoutePath();
	const history = useHistory();

	const sendMFACode = useCallback(async () => {
		if (mfaPreconditionData) {
			if (method === MFA_METHODS_ENUM.EMAIL || method === MFA_METHODS_ENUM.SMS) {
				const fingerprint = await FingerprintJS.load();
				const { visitorId } = await fingerprint.get();
				try {
					await getMFACode({ device_id: visitorId, login: mfaPreconditionData.email, type: method });
				} catch (e) {
					showToast.error('Something went wrong!');
				}
			}
		} else history.push(generateRoutePath(RoutePaths.AUTH.LOGIN));
	}, [history, mfaPreconditionData, method]);

	const login = useCallback(
		async (values: { code: string | AuthenticationResponseJSON }, formikHelpers?: FormikHelpers<{ code: string }>) => {
			if (mfaPreconditionData) {
				try {
					const fingerprint = await FingerprintJS.load();
					const { visitorId } = await fingerprint.get();
					await loginWithMFA({
						mfa_codes: { [mfaCodeKey[method]]: values.code },
						password: mfaPreconditionData.password,
						login: mfaPreconditionData?.email,
						device_id: visitorId,
						remember_me: false,
					});
				} catch (e) {
					formikHelpers
						? formikHelpers.setErrors({ code: 'Oops. Something went wrong.' })
						: showToast.error('Oops. Something went wrong.');
				}
			} else history.push(generateRoutePath(RoutePaths.AUTH.LOGIN));
		},
		[history, mfaPreconditionData, method],
	);

	return { login, sendMFACode, isSendingMFACode, isLoggingIn };
};
